import { colours } from './trustLibrary';

export const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;
export const MAX_UNIFORMS = 100;
export const SUPPORTED_TEMPLATE_IDS = new Set(['scrub-vneck-v1', 'healthcare-tunic-v1']);

// Legacy names describe a complete body/trim combination. They must never be
// reused as the name of one independent colour in the custom-uniform editor.
const colourNames = new Map([
  ['#FFFFFF', 'White'], ['#1E1E1E', 'Black'], ['#000000', 'Black'],
  ['#6B7280', 'Grey'], ['#374151', 'Dark grey'], ['#1E3A5F', 'Navy'],
  ['#005EB8', 'NHS blue'], ['#2070CC', 'Royal blue'], ['#84D2F7', 'Pale blue'],
  ['#6AAFDB', 'Light blue'], ['#006747', 'Paramedic green'],
  ['#EC4899', 'Pink'], ['#C8305A', 'Red'], ['#A8305A', 'Ruby'],
]);

export const palette = [...new Map([
  ...colours.filter(c => c.id !== 'no_uniform').flatMap(c => [c.body, c.trim]),
  ...colourNames.keys(),
].filter(hex => HEX_PATTERN.test(hex)).map(hex => {
  const normalised = hex.toUpperCase();
  return [normalised, { name: colourNames.get(normalised) || `Colour ${normalised}`, hex: normalised }];
})).values()].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) || a.hex.localeCompare(b.hex));

export function newUniformId() {
  return `uniform_${crypto.randomUUID().replaceAll('-', '')}`;
}

export function makeUniform(source = {}, id = newUniformId()) {
  const templateId = SUPPORTED_TEMPLATE_IDS.has(source.templateId) ? source.templateId : 'scrub-vneck-v1';
  return { id, name: source.name ? `${source.name} copy` : 'New uniform', templateId, bodyHex: source.bodyHex || '#2070CC', trimHex: source.trimHex || '#84D2F7', ...(templateId === 'scrub-vneck-v1' && source.pocketPipingHex ? { pocketPipingHex: source.pocketPipingHex } : {}), badge: { assetId: source.badge?.assetId || 'nhs-standard', visible: source.badge?.visible !== false } };
}

export function validateCatalogue(definitions, assignments = {}) {
  if (!definitions || typeof definitions !== 'object' || Array.isArray(definitions)) throw new Error('The uniform catalogue must be an object.');
  if (Object.keys(definitions).length > MAX_UNIFORMS) throw new Error('A Trust can have no more than 100 custom uniforms.');
  for (const [id, item] of Object.entries(definitions)) {
    if (!/^uniform_[a-z0-9]+(?:_[a-z0-9]+)*$/.test(id) || id.length > 80 || item.id !== id) throw new Error('A uniform has an invalid or mismatched ID.');
    if (!item.name?.trim() || item.name.trim().length > 120) throw new Error('Each uniform needs a name of at most 120 characters.');
    if (!SUPPORTED_TEMPLATE_IDS.has(item.templateId)) throw new Error('Unsupported uniform template.');
    if (!HEX_PATTERN.test(item.bodyHex) || !HEX_PATTERN.test(item.trimHex)) throw new Error('Body and trim must use six-digit HEX colours.');
    if (item.templateId === 'healthcare-tunic-v1' && item.pocketPipingHex != null) throw new Error('Healthcare tunic pocket piping follows the trim colour and cannot be set separately.');
    if (item.pocketPipingHex != null && !HEX_PATTERN.test(item.pocketPipingHex)) throw new Error('Pocket piping must use a six-digit HEX colour.');
    if (!['nhs-standard', 'none'].includes(item.badge?.assetId) || typeof item.badge?.visible !== 'boolean') throw new Error('Choose an approved badge.');
  }
  for (const [key, id] of Object.entries(assignments)) {
    if (id && !Object.prototype.hasOwnProperty.call(definitions, id)) throw new Error(`Role ${key} references an unavailable uniform.`);
  }
  return true;
}

export function applyUniformEdits(configuration, definitions, assignments = {}) {
  validateCatalogue(definitions, assignments);
  const roles = { ...(configuration.roles || {}) };
  for (const [key, id] of Object.entries(assignments)) {
    // Creating an appearance override must not silently include a missing role.
    const previous = roles[key] || { included: false };
    const uniform = { ...(previous.uniform || {}) };
    // Explicit null clears a reference; omission is preserved by older clients.
    uniform.uniformDefinitionId = id || null;
    roles[key] = { ...previous, uniform };
  }
  for (const [key, role] of Object.entries(roles)) {
    const id = role.uniform?.uniformDefinitionId;
    if (id && !Object.prototype.hasOwnProperty.call(definitions, id)) throw new Error(`Role ${key} references an unavailable uniform.`);
  }
  return { ...configuration, roles, uniforms: definitions };
}

export function assertUnchangedConfiguration(original, latest) {
  if (JSON.stringify(original) !== JSON.stringify(latest)) throw new Error('This Trust configuration has changed since you opened it. Reload the latest draft before saving so another administrator’s work is not overwritten.');
  if (latest.status !== 'draft') throw new Error('This Trust profile is locked. Return it to draft before editing uniforms.');
}
